import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet, Save
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { resolvePolizas, polizasTotal, polizasPayload, resolveAlquiler, alquilerMensual, alquilerTotal } from "./gastosAdminCalc";

// ─── Gastos Admin Detallado Section ──────────────────────────────────────────
export function GastosAdminDetalladoSection({ budgetId, budget, moCalif, moNoCalif, moEsp, meses, readOnly, hhLive }) {
  const fmtPyg = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;
  const init = budget?.gastos_admin_detallado || {};
  const { user } = useAuth();
  const isAdmin = user?.role === "Superadministrador" || user?.role === "Administrador";
  // C.3 — Estado para sugerencias persistidas
  const [sugerencias, setSugerencias] = useState(null);
  const [savingSugerencia, setSavingSugerencia] = useState(false);

  // PDF REV1 A.1: priorizamos los valores LIVE de HorasHombreSection sobre los del budget guardado.
  // Esto permite que IPS, Seguro, EPI, Acta médica recalculen sin guardar.
  const mesesNum = parseFloat(hhLive?.meses ?? meses ?? budget?.hh_meses_duracion ?? 1) || 1;
  const hombresNum = parseInt(hhLive?.hombres ?? budget?.hombres_obra ?? 1) || 1;
  const hsLvNum = parseFloat(hhLive?.hsLv ?? budget?.hh_hs_lv ?? 40);
  const hsSabNum = parseFloat(hhLive?.hsSab ?? budget?.hh_hs_sab ?? 5);
  const hsMensXHombre = hhLive?.hsMensXHombre ?? ((hsLvNum + hsSabNum) * 4);
  // P1-linked: Total HH = meses × hombres × hs/hombre/mes
  const totalHH = hhLive?.totalHH ?? (mesesNum * hombresNum * hsMensXHombre);
  // P12: Factor anual automático si proyecto > 12 meses
  const factorAnual = Math.max(1, mesesNum / 12);

  // P9: IPS por tarifa/hora (en vez de % sobre planilla)
  const [tarifaHoraIps, setTarifaHoraIps] = useState(init.tarifa_hora_ips ?? 0);

  // P11: Seguro por persona (en vez de mensual)
  const [seguroXPersona, setSeguroXPersona] = useState(init.seguro_acc_x_persona ?? 0);
  const [factorRotacion, setFactorRotacion] = useState(init.factor_rotacion ?? 0);

  // EPIs — P13: gasto mensual × meses × obreros × factor_rotacion
  const [epiItems, setEpiItems] = useState(init.epi_items?.length > 0 ? init.epi_items : [
    { nombre: "Casco de seguridad", gasto_mensual: 0 },
    { nombre: "Guantes de trabajo", gasto_mensual: 0 },
    { nombre: "Chaleco reflectivo", gasto_mensual: 0 },
    { nombre: "Antiparras", gasto_mensual: 0 },
    { nombre: "Chaqueta / Mameluco", gasto_mensual: 0 },
  ]);
  const [factorDesgaste, setFactorDesgaste] = useState(init.factor_desgaste_epi ?? 1.0);
  const [gastosMenores, setGastosMenores] = useState(init.gastos_menores || 0);
  const [factorMenores, setFactorMenores] = useState(init.factor_menores ?? 1.0);
  const [aptaMedica, setAptaMedica] = useState(init.apta_medica || 0);
  const [polizas, setPolizas] = useState(() => resolvePolizas(init));
  const [alquilerItems, setAlquilerItems] = useState(() => resolveAlquiler(init));

  // Equipo de Seguridad — P14: solo sumatoria × factor desgaste (sin cantPersonal)
  const [equipSegItems, setEquipSegItems] = useState(init.equip_seg_items?.length > 0 ? init.equip_seg_items : [
    { nombre: "Línea de vida Karan", precio: 686100 },
    { nombre: "Arnés con protector lumbar", precio: 808300 },
  ]);
  const [factorDesgasteSeg, setFactorDesgasteSeg] = useState(init.factor_desgaste_seg ?? 0.3);

  // Herramientas — P15: solo sumatoria × factor desgaste (sin cantPersonal)
  const [herramientasItems, setHerramientasItems] = useState(init.herramientas_items?.length > 0 ? init.herramientas_items : [
    { nombre: "Percutor", precio: 1500000 },
    { nombre: "Amoladora", precio: 300000 },
  ]);
  const [factorDesgasteHerr, setFactorDesgasteHerr] = useState(init.factor_desgaste_herr ?? 0.5);

  const [saving, setSaving] = useState(false);

  // C.3 — Cargar sugerencias y prepoblar campos vacíos en budgets NUEVOS.
  // Para budgets existentes (init.usar_detallado === true) NO sobrescribimos.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/budgets/gastos-admin/sugerencias`, opts);
        if (cancelled) return;
        setSugerencias(data);
        const sv = data?.values || {};
        const isNewBudget = !init.usar_detallado;
        if (isNewBudget) {
          if (sv.ips_gs_hora != null && !init.tarifa_hora_ips) {
            setTarifaHoraIps(sv.ips_gs_hora);
          }
          if (sv.seguro_obreros_costo_persona != null && !init.seguro_acc_x_persona) {
            setSeguroXPersona(sv.seguro_obreros_costo_persona);
          }
          if (sv.factor_rotacion_personal != null && !init.factor_rotacion) {
            setFactorRotacion(sv.factor_rotacion_personal);
          }
          if (sv.gastos_menores_valor_mensual != null && !init.gastos_menores) {
            setGastosMenores(sv.gastos_menores_valor_mensual);
          }
          if (sv.apta_medica_costo != null && !init.apta_medica) {
            setAptaMedica(sv.apta_medica_costo);
          }
          if (sv.poliza_seguro_integral != null && !init.poliza && !Array.isArray(init.polizas)) {
            setPolizas(p => p.map((x, i) => (i === 0 ? { ...x, monto: sv.poliza_seguro_integral } : x)));
          }
          if (sv.equipos_seguridad_factor_desgaste != null && init.factor_desgaste_seg == null) {
            setFactorDesgasteSeg(sv.equipos_seguridad_factor_desgaste);
          }
          if (sv.herramientas_factor_desgaste != null && init.factor_desgaste_herr == null) {
            setFactorDesgasteHerr(sv.herramientas_factor_desgaste);
          }
        }
      } catch (e) {
        // Silencioso: si no hay catálogo o falla, seguimos con defaults.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // C.3 — Actualizar catálogo de sugerencias (solo Admin/Superadmin)
  const updateSugerenciasGenerales = async () => {
    if (!isAdmin) return;
    setSavingSugerencia(true);
    try {
      const payload = {
        values: {
          factor_rotacion_personal: parseFloat(factorRotacion) || 0,
          ips_gs_hora: parseFloat(tarifaHoraIps) || 0,
          apta_medica_costo: parseFloat(aptaMedica) || 0,
          seguro_obreros_costo_persona: parseFloat(seguroXPersona) || 0,
          epis_gasto_mensual: epiItems.reduce((s, i) => s + (parseFloat(i.gasto_mensual || i.precio) || 0), 0),
          equipos_seguridad_factor_desgaste: parseFloat(factorDesgasteSeg) || 0,
          herramientas_factor_desgaste: parseFloat(factorDesgasteHerr) || 0,
          gastos_menores_valor_mensual: parseFloat(gastosMenores) || 0,
          poliza_seguro_integral: polizasTotal(polizas),
        },
      };
      const { data } = await axios.post(`${API}/budgets/gastos-admin/sugerencias/bulk`, payload, opts);
      setSugerencias(data);
      toast.success("Sugerencias generales actualizadas para futuros presupuestos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar sugerencias");
    } finally {
      setSavingSugerencia(false);
    }
  };

  // ── Cálculos corregidos ──────────────────────────────────────────────────────

  // P9: IPS = total_HH × tarifa_hora_ips
  const ipsTotal = totalHH * (parseFloat(tarifaHoraIps) || 0);

  // P11: Seguro = obreros × costo_por_persona × (1 + factor_rotacion) × factor_anual
  const seguroTotal = hombresNum * (parseFloat(seguroXPersona) || 0) * (1 + (parseFloat(factorRotacion) || 0)) * factorAnual;

  // P13: EPIs = sum(gasto_mensual_item) × meses × obreros × (1 + factor_rotacion)
  const epiGastoMensualTotal = epiItems.reduce((s, i) => s + parseFloat(i.gasto_mensual || i.precio || 0), 0);
  const epiTotal = epiGastoMensualTotal * mesesNum * hombresNum * (1 + (parseFloat(factorRotacion) || 0));

  // PDF REV1 #9: Gastos menores ligados a meses de obra → meses × valor_mensual × factor
  const menoresTotal = (parseFloat(gastosMenores) || 0) * mesesNum * (parseFloat(factorMenores) || 1);

  // P10: Acta médica = costo_unitario × obreros × (1 + factor_rotacion) × factor_anual
  const aptaTotal = (parseFloat(aptaMedica) || 0) * hombresNum * (1 + (parseFloat(factorRotacion) || 0)) * factorAnual;

  const polizaTotal = polizasTotal(polizas);

  // Alquileres: mismo patrón que gastos menores → gasto mensual × meses de obra (misma fuente mesesNum).
  const alquilerMensualTotal = alquilerMensual(alquilerItems);
  const alquilerTotalGs = alquilerTotal(alquilerItems, mesesNum);

  // P14: Equipos seguridad = sum(precios) × factor_desgaste (sin ligarlo al tiempo ni a la gente)
  const equipSegTotal = equipSegItems.reduce((s, i) => s + parseFloat(i.precio || 0), 0) * (parseFloat(factorDesgasteSeg) || 0);

  // P15: Herramientas = sum(precios) × factor_desgaste
  const herramientasTotal = herramientasItems.reduce((s, i) => s + parseFloat(i.precio || 0), 0) * (parseFloat(factorDesgasteHerr) || 0);

  const totalDetallado = ipsTotal + seguroTotal + epiTotal + menoresTotal + aptaTotal + polizaTotal + alquilerTotalGs + equipSegTotal + herramientasTotal;

  const save = async () => {
    if (!budgetId) { toast.error("Guardá el presupuesto primero"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${budgetId}`, {
        gastos_admin_detallado: {
          usar_detallado: true,
          tarifa_hora_ips: parseFloat(tarifaHoraIps) || 0,
          seguro_acc_x_persona: parseFloat(seguroXPersona) || 0,
          factor_rotacion: parseFloat(factorRotacion) || 0,
          epi_items: epiItems,
          factor_desgaste_epi: parseFloat(factorDesgaste) || 1.0,
          gastos_menores: parseFloat(gastosMenores) || 0,
          factor_menores: parseFloat(factorMenores) || 1.0,
          apta_medica: parseFloat(aptaMedica) || 0,
          ...polizasPayload(polizas),
          alquiler_items: alquilerItems.map(a => ({ nombre: a.nombre, gasto_mensual: parseFloat(a.gasto_mensual) || 0 })),
          equip_seg_items: equipSegItems,
          factor_desgaste_seg: parseFloat(factorDesgasteSeg) || 0,
          herramientas_items: herramientasItems,
          factor_desgaste_herr: parseFloat(factorDesgasteHerr) || 0,
        },
      }, { withCredentials: true });
      toast.success("Estimación de gastos guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const numInput = (value, onChange, opts = {}) => (
    <input
      type="text"
      inputMode="numeric"
      value={value === 0 || value === "0" ? "" : value}
      placeholder="0"
      onChange={e => {
        const raw = e.target.value.replace(/[^\d.]/g, "");
        onChange(raw === "" ? 0 : raw);
      }}
      className={`border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right focus:ring-1 focus:ring-purple-300 ${opts.className || "w-28"}`}
      data-testid={opts.testid}
    />
  );

  return (
    <div className="mt-4 border-t border-dashed border-purple-200 pt-4" data-testid="gastos-admin-detallado-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase font-bold tracking-wider text-purple-700">Estimación Detallada de Gastos</p>
          <p className="text-[9px] text-gray-400 mt-0.5">
            Base: {hombresNum} hombres · {mesesNum} meses · {totalHH.toLocaleString("es-PY", {maximumFractionDigits:0})} H.H. total
            {mesesNum > 12 && <span className="ml-1 text-amber-600 font-semibold">· Factor anual: ×{factorAnual.toFixed(2)}</span>}
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <button
                onClick={updateSugerenciasGenerales}
                disabled={savingSugerencia}
                className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-2 py-1 rounded-lg disabled:opacity-50 transition-colors"
                title="Guarda estos valores como sugerencia para los próximos presupuestos"
                data-testid="update-sugerencias-btn"
              >
                {savingSugerencia ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                Actualizar sugerencia general
              </button>
            )}
            <button onClick={save} disabled={saving || !budgetId}
              className="flex items-center gap-1 text-xs text-white bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded-lg disabled:opacity-50 transition-colors"
              data-testid="save-gastos-detallado-btn">
              {saving ? <Loader2 size={11} className="animate-spin" /> : null} Guardar estimación
            </button>
          </div>
        )}
      </div>

      {/* Factor de rotación — compartido por varios ítems */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-blue-700">Factor de rotación de personal</span>
        {!readOnly ? (
          <div className="flex items-center gap-1">
            {numInput(factorRotacion, setFactorRotacion, { className: "w-16", testid: "factor-rotacion-input" })}
            <span className="text-[10px] text-blue-500">(ej: 0.5 = 50% rotación)</span>
          </div>
        ) : <span className="text-sm font-bold text-blue-700">{factorRotacion}</span>}
        <span className="text-[10px] text-blue-400 italic">Aplica a: Seguro, EPI, Acta médica (NO al IPS)</span>
      </div>

      <div className="space-y-2">
        {/* IPS — P9: total_HH × tarifa_hora */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-700">IPS</span>
              {!readOnly ? (
                <div className="flex items-center gap-1">
                  {numInput(tarifaHoraIps, setTarifaHoraIps, { className: "w-24", testid: "ips-tarifa-input" })}
                  <span className="text-xs text-gray-500">Gs./hora</span>
                </div>
              ) : <span className="text-xs text-gray-500">{fmtPyg(tarifaHoraIps)}/hora</span>}
              <span className="text-[10px] text-gray-400">Total H.H.: {totalHH.toLocaleString("es-PY", {maximumFractionDigits:0})} hs.</span>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-gray-800" data-testid="ips-total">{fmtPyg(ipsTotal)}</span>
              <p className="text-[9px] text-gray-400">= {totalHH.toLocaleString("es-PY",{maximumFractionDigits:0})} H.H. × {fmtPyg(tarifaHoraIps)}/h</p>
            </div>
          </div>
        </div>

        {/* Seguro contra accidentes — P11: obreros × costo/persona × (1 + rot) × factor_anual */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-medium text-gray-700">Seguro contra accidentes</span>
            <div className="flex items-center gap-2 flex-wrap">
              {!readOnly && (
                <div className="flex items-center gap-1">
                  {numInput(seguroXPersona, setSeguroXPersona, { testid: "seguro-x-persona-input" })}
                  <span className="text-[10px] text-gray-500">Gs./persona</span>
                </div>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="seguro-total">{fmtPyg(seguroTotal)}</span>
                <p className="text-[9px] text-gray-400">= {hombresNum}p × {fmtPyg(seguroXPersona)} × (1+{factorRotacion}) × {factorAnual.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* EPI / Uniforme — P13: gasto mensual × meses × obreros × (1 + rot) */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className="text-xs font-medium text-gray-700">EPI / Uniforme</span>
              <p className="text-[9px] text-gray-400">Ingresá el gasto mensual por persona de cada ítem</p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <button onClick={() => setEpiItems(p => [...p, { nombre: "", gasto_mensual: 0 }])}
                  className="text-[10px] text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex items-center gap-0.5">
                  <Plus size={10} /> agregar
                </button>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="epi-total">{fmtPyg(epiTotal)}</span>
                <p className="text-[9px] text-gray-400">= {fmtPyg(epiGastoMensualTotal)}/mes × {mesesNum}m × {hombresNum}p × (1+{factorRotacion})</p>
              </div>
            </div>
          </div>
          <div className="space-y-1 ml-2">
            {epiItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {readOnly ? (
                  <span className="flex-1 text-gray-600">{item.nombre} — {fmtPyg(item.gasto_mensual || item.precio || 0)}/mes</span>
                ) : (
                  <>
                    <input value={item.nombre} onChange={e => setEpiItems(p => p.map((r, ri) => ri === i ? { ...r, nombre: e.target.value } : r))}
                      className="flex-1 border border-gray-200 rounded px-1.5 py-0.5 text-xs" placeholder="Ítem EPI" />
                    <span className="text-[10px] text-gray-400">Gs./mes:</span>
                    <input
                      type="text" inputMode="numeric"
                      value={item.gasto_mensual === 0 || item.gasto_mensual === "0" ? "" : (item.gasto_mensual || item.precio || "")}
                      placeholder="0"
                      onChange={e => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        setEpiItems(p => p.map((r, ri) => ri === i ? { ...r, gasto_mensual: raw === "" ? 0 : parseInt(raw), precio: undefined } : r));
                      }}
                      className="w-28 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right" />
                    <button onClick={() => setEpiItems(p => p.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600"><X size={11} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Gastos menores — PDF REV1 #9: ligado a meses de obra */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-medium text-gray-700">Gastos menores (mecha, tornillos, etc.) <span className="text-[10px] text-gray-400">— Gs./mes</span></span>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <>
                  {numInput(gastosMenores, setGastosMenores, { testid: "gastos-menores-input" })}
                  <div className="flex items-center gap-1">
                    <label className="text-[10px] text-gray-500">× Factor</label>
                    <input type="number" min="0" step="0.1" value={factorMenores} onChange={e => setFactorMenores(e.target.value)}
                      className="w-14 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-center" data-testid="gastos-menores-factor-input" />
                  </div>
                </>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="gastos-menores-total">{fmtPyg(menoresTotal)}</span>
                <p className="text-[9px] text-gray-400">= {fmtPyg(gastosMenores)}/mes × {mesesNum}m × {factorMenores}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Apta médica — P10: costo × obreros × (1 + rot) × factor_anual */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-medium text-gray-700">
              Apta médica <span className="text-[10px] text-gray-400">(x{hombresNum} personas)</span>
            </span>
            <div className="flex items-center gap-2">
              {!readOnly && numInput(aptaMedica, setAptaMedica, { testid: "apta-medica-input" })}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="apta-total">{fmtPyg(aptaTotal)}</span>
                <p className="text-[9px] text-gray-400">= {fmtPyg(aptaMedica)}/p × {hombresNum}p × (1+{factorRotacion}) × {factorAnual.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Póliza */}
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="polizas-block">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className="text-xs font-medium text-gray-700">Pólizas / Seguros</span>
              <p className="text-[9px] text-gray-400">Importe fijo por póliza — no ligado al tiempo</p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <button onClick={() => setPolizas(p => [...p, { concepto: "", monto: 0 }])} data-testid="poliza-add-btn"
                  className="text-[10px] text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex items-center gap-0.5">
                  <Plus size={10} /> agregar
                </button>
              )}
              <span className="text-xs font-mono font-bold text-gray-800" data-testid="polizas-total">{fmtPyg(polizaTotal)}</span>
            </div>
          </div>
          <div className="space-y-1 ml-2">
            {polizas.length === 0 && <p className="text-[10px] text-gray-400">Sin pólizas</p>}
            {polizas.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`poliza-row-${i}`}>
                {readOnly ? (
                  <span className="flex-1 text-gray-600">{p.concepto} — {fmtPyg(p.monto)}</span>
                ) : (
                  <>
                    <input value={p.concepto} onChange={e => setPolizas(prev => prev.map((r, ri) => ri === i ? { ...r, concepto: e.target.value } : r))}
                      className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-0.5 text-xs" placeholder="Tipo / clase de póliza" data-testid={`poliza-concepto-${i}`} />
                    {numInput(p.monto, v => setPolizas(prev => prev.map((r, ri) => ri === i ? { ...r, monto: v } : r)), { className: "w-28", testid: `poliza-monto-${i}` })}
                    <button onClick={() => setPolizas(prev => prev.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600" data-testid={`poliza-del-${i}`}><X size={11} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Alquileres — gasto mensual × meses de obra (mismo patrón que gastos menores) */}
        <div className="bg-gray-50 rounded-lg p-2.5" data-testid="alquiler-block">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className="text-xs font-medium text-gray-700">Alquiler de andamios / maquinaria / elevadores</span>
              <p className="text-[9px] text-gray-400">Gs./mes — ligado al tiempo de obra</p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <button onClick={() => setAlquilerItems(p => [...p, { nombre: "", gasto_mensual: 0 }])} data-testid="alquiler-add-btn"
                  className="text-[10px] text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex items-center gap-0.5">
                  <Plus size={10} /> agregar
                </button>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="alquiler-total">{fmtPyg(alquilerTotalGs)}</span>
                <p className="text-[9px] text-gray-400">= {fmtPyg(alquilerMensualTotal)}/mes × {mesesNum}m</p>
              </div>
            </div>
          </div>
          <div className="space-y-1 ml-2">
            {alquilerItems.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`alquiler-row-${i}`}>
                {readOnly ? (
                  <span className="flex-1 text-gray-600">{a.nombre} — {fmtPyg(a.gasto_mensual)}/mes</span>
                ) : (
                  <>
                    <input value={a.nombre} onChange={e => setAlquilerItems(prev => prev.map((r, ri) => ri === i ? { ...r, nombre: e.target.value } : r))}
                      className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-0.5 text-xs" placeholder="Equipo en alquiler" data-testid={`alquiler-nombre-${i}`} />
                    <span className="text-[10px] text-gray-400">Gs./mes:</span>
                    {numInput(a.gasto_mensual, v => setAlquilerItems(prev => prev.map((r, ri) => ri === i ? { ...r, gasto_mensual: v } : r)), { className: "w-28", testid: `alquiler-monto-${i}` })}
                    <button onClick={() => setAlquilerItems(prev => prev.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600" data-testid={`alquiler-del-${i}`}><X size={11} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Equipo de Seguridad — P14: sum × factor_desgaste (sin tiempo, sin personas) */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className="text-xs font-medium text-gray-700">Equipo de Seguridad (Línea de vida, Arnés, Karan…)</span>
              <p className="text-[9px] text-gray-400">Solo factor de desgaste — no ligado al tiempo</p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <label className="text-[10px] text-gray-500">Factor desgaste</label>
                  <input type="number" min="0" step="0.05" value={factorDesgasteSeg} onChange={e => setFactorDesgasteSeg(e.target.value)}
                    className="w-14 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-center" data-testid="equip-seg-factor-input" />
                  <button onClick={() => setEquipSegItems(p => [...p, { nombre: "", precio: 0 }])}
                    className="text-[10px] text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex items-center gap-0.5">
                    <Plus size={10} /> agregar
                  </button>
                </div>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="equip-seg-total">{fmtPyg(equipSegTotal)}</span>
                <p className="text-[9px] text-gray-400">= Σ precios × {factorDesgasteSeg}</p>
              </div>
            </div>
          </div>
          <div className="space-y-1 ml-2">
            {equipSegItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {readOnly ? (
                  <span className="flex-1 text-gray-600">{item.nombre} — {fmtPyg(item.precio)}</span>
                ) : (
                  <>
                    <input value={item.nombre} onChange={e => setEquipSegItems(p => p.map((r, ri) => ri === i ? { ...r, nombre: e.target.value } : r))}
                      className="flex-1 border border-gray-200 rounded px-1.5 py-0.5 text-xs" placeholder="Equipo de seguridad" />
                    <input type="text" inputMode="numeric"
                      value={item.precio === 0 || item.precio === "0" ? "" : item.precio}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        setEquipSegItems(p => p.map((r, ri) => ri === i ? { ...r, precio: raw === "" ? 0 : parseInt(raw) } : r));
                      }}
                      className="w-32 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right" placeholder="Precio unit." />
                    <button onClick={() => setEquipSegItems(p => p.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600"><X size={11} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Herramientas — P15: sum × factor_desgaste (sin tiempo, sin personas) */}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <span className="text-xs font-medium text-gray-700">Herramientas (Percutor, Amoladora…)</span>
              <p className="text-[9px] text-gray-400">Solo factor de desgaste — no ligado al tiempo</p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <label className="text-[10px] text-gray-500">Factor desgaste</label>
                  <input type="number" min="0" step="0.05" value={factorDesgasteHerr} onChange={e => setFactorDesgasteHerr(e.target.value)}
                    className="w-14 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-center" data-testid="herr-factor-input" />
                  <button onClick={() => setHerramientasItems(p => [...p, { nombre: "", precio: 0 }])}
                    className="text-[10px] text-purple-600 hover:bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 flex items-center gap-0.5">
                    <Plus size={10} /> agregar
                  </button>
                </div>
              )}
              <div className="text-right">
                <span className="text-xs font-mono font-bold text-gray-800" data-testid="herr-total">{fmtPyg(herramientasTotal)}</span>
                <p className="text-[9px] text-gray-400">= Σ precios × {factorDesgasteHerr}</p>
              </div>
            </div>
          </div>
          <div className="space-y-1 ml-2">
            {herramientasItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {readOnly ? (
                  <span className="flex-1 text-gray-600">{item.nombre} — {fmtPyg(item.precio)}</span>
                ) : (
                  <>
                    <input value={item.nombre} onChange={e => setHerramientasItems(p => p.map((r, ri) => ri === i ? { ...r, nombre: e.target.value } : r))}
                      className="flex-1 border border-gray-200 rounded px-1.5 py-0.5 text-xs" placeholder="Herramienta" />
                    <input type="text" inputMode="numeric"
                      value={item.precio === 0 || item.precio === "0" ? "" : item.precio}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        setHerramientasItems(p => p.map((r, ri) => ri === i ? { ...r, precio: raw === "" ? 0 : parseInt(raw) } : r));
                      }}
                      className="w-32 border border-gray-200 rounded px-1.5 py-0.5 text-xs text-right" placeholder="Precio unit." />
                    <button onClick={() => setHerramientasItems(p => p.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600"><X size={11} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="bg-purple-700 rounded-lg px-3 py-2 flex items-center justify-between">
          <span className="text-xs font-bold text-white">TOTAL GASTOS ADMIN. ESTIMADOS</span>
          <span className="text-sm font-bold text-white font-mono" data-testid="total-gastos-estimados">{fmtPyg(totalDetallado)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Gastos Administrativos Section ──────────────────────────────────────────
export function GastosAdminSection({ budgetId, initItems, readOnly, budget, hhLive }) {
  const fmtPyg = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;
  const [gaItems, setGaItems] = useState(initItems?.length > 0 ? initItems : [
    { concepto: "Honorarios profesionales", monto: 0 },
    { concepto: "Movilidad y viáticos", monto: 0 },
    { concepto: "Gastos de gestión", monto: 0 },
  ]);
  const [saving, setSaving] = useState(false);
  const [showDetallado, setShowDetallado] = useState(budget?.gastos_admin_detallado?.usar_detallado || false);

  const total = gaItems.reduce((s, i) => s + (parseFloat(i.monto) || 0), 0);

  const save = async () => {
    if (!budgetId) { toast.error("Guardá el presupuesto primero"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${budgetId}`, { gastos_admin_items: gaItems }, { withCredentials: true });
      toast.success("Gastos administrativos guardados");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="gastos-admin-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center"><Percent size={13} className="text-purple-600" /></div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Estimación de Gastos Administrativos</h3>
            <p className="text-[11px] text-gray-400">Honorarios, movilidad, gestión y otros gastos operativos</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowDetallado(v => !v)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${showDetallado ? "bg-purple-100 text-purple-700 border-purple-300" : "text-gray-500 border-gray-200 hover:bg-gray-50"}`}
            data-testid="toggle-detallado-btn">
            {showDetallado ? "Ocultar estimación detallada" : "Estimación detallada (IPS, EPI, Seguro...)"}
          </button>
          {!readOnly && (
            <>
              <button onClick={() => setGaItems(p => [...p, { concepto: "", monto: 0 }])}
                className="flex items-center gap-1 text-xs text-purple-600 hover:bg-purple-50 px-2 py-1 rounded-lg border border-purple-200 transition-colors" data-testid="add-gasto-btn">
                <Plus size={12} /> Agregar ítem
              </button>
              <button onClick={save} disabled={saving || !budgetId}
                className="flex items-center gap-1 text-xs text-white bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded-lg disabled:opacity-50 transition-colors" data-testid="save-gastos-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                Guardar
              </button>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-purple-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-purple-700">Concepto</th>
              <th className="px-3 py-2 text-right font-semibold text-purple-700 w-40">Monto (Gs.)</th>
              {!readOnly && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {gaItems.map((it, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-3 py-1.5">
                  {readOnly ? <span>{it.concepto}</span> : (
                    <input value={it.concepto} onChange={e => setGaItems(p => p.map((r, ri) => ri === i ? { ...r, concepto: e.target.value } : r))}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-purple-300" placeholder="Nombre del gasto" />
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {readOnly ? <span className="text-right block font-mono">{fmtPyg(it.monto)}</span> : (
                    <input type="number" value={it.monto} onChange={e => setGaItems(p => p.map((r, ri) => ri === i ? { ...r, monto: e.target.value } : r))}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-right focus:ring-1 focus:ring-purple-300" />
                  )}
                </td>
                {!readOnly && (
                  <td className="px-1 py-1 text-center">
                    <button onClick={() => setGaItems(p => p.filter((_, ri) => ri !== i))} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-purple-200">
            <tr>
              <td className="px-3 py-2 font-bold text-purple-700">TOTAL GASTOS ADMINISTRATIVOS</td>
              <td className="px-3 py-2 font-bold text-purple-700 text-right font-mono">{fmtPyg(total)}</td>
              {!readOnly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Keep detallado always mounted to preserve state — toggle visibility only */}
      <div className={showDetallado ? "" : "hidden"}>
        <GastosAdminDetalladoSection
          budgetId={budgetId}
          budget={budget}
          moCalif={budget?.hh_mo_calificada || []}
          moNoCalif={budget?.hh_mo_no_calificada || []}
          moEsp={budget?.hh_mo_especial || []}
          meses={budget?.hh_meses_duracion || 1}
          readOnly={readOnly}
          hhLive={hhLive}
        />
      </div>
    </div>
  );
}

